import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaCog,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaPlug,
  FaShieldAlt,
  FaKey
} from 'react-icons/fa';
import { paymentService } from '../../../../services/paymentService';
import { toast } from 'react-toastify';

// Verify import
if (!paymentService || !paymentService.getGatewayConfig) {
  console.error('PaymentService import error: paymentService is not properly imported');
}

const PaymentGatewayConfig = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ razorpay: false, phonepe: false });
  const [showSecrets, setShowSecrets] = useState({ razorpay: false, phonepe: false });
  const [config, setConfig] = useState({
    razorpay: {
      enabled: false,
      keyId: '',
      keySecret: '',
      webhookSecret: '',
      environment: 'sandbox', // sandbox or production
      testMode: true
    },
    phonepe: {
      enabled: false,
      merchantId: '',
      saltKey: '',
      saltIndex: '1',
      environment: 'sandbox',
      testMode: true
    }
  });
  const [connectionStatus, setConnectionStatus] = useState({
    razorpay: null,
    phonepe: null
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getGatewayConfig();
      if (data) {
        setConfig({
          razorpay: {
            ...config.razorpay,
            ...data.razorpay
          },
          phonepe: {
            ...config.phonepe,
            ...data.phonepe
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
      if (error.message?.includes('404') || error.message?.includes('Failed to fetch')) {
        toast.error('Payment gateway routes not available. Please ensure the backend server is restarted.');
      } else {
        toast.error('Failed to load gateway configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (gateway, field, value) => {
    setConfig(prev => ({
      ...prev,
      [gateway]: {
        ...prev[gateway],
        [field]: value
      }
    }));
  };

  const handleTestConnection = async (gateway) => {
    try {
      setTesting(prev => ({ ...prev, [gateway]: true }));
      const result = await paymentService.testGatewayConnection(gateway);
      setConnectionStatus(prev => ({
        ...prev,
        [gateway]: result.success ? 'connected' : 'failed'
      }));
      if (result.success) {
        toast.success(`${gateway.toUpperCase()} connection successful!`);
      } else {
        toast.error(`${gateway.toUpperCase()} connection failed: ${result.message}`);
      }
    } catch (error) {
      setConnectionStatus(prev => ({
        ...prev,
        [gateway]: 'failed'
      }));
      toast.error(`Failed to test ${gateway.toUpperCase()} connection: ${error.message}`);
    } finally {
      setTesting(prev => ({ ...prev, [gateway]: false }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await paymentService.updateGatewayConfig(config);
      toast.success('Gateway configuration saved successfully!');
      if (onClose) onClose();
    } catch (error) {
      toast.error('Failed to save configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const GatewaySection = ({ gateway, title, icon: Icon }) => {
    const gatewayConfig = config[gateway];
    const isTesting = testing[gateway];
    const status = connectionStatus[gateway];
    const showSecret = showSecrets[gateway];

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-gray-200 p-6 mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-xs text-gray-500">Configure {title} payment gateway</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {status && (
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                status === 'connected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {status === 'connected' ? (
                  <>
                    <FaCheckCircle /> Connected
                  </>
                ) : (
                  <>
                    <FaTimesCircle /> Failed
                  </>
                )}
              </div>
            )}
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={gatewayConfig.enabled}
                onChange={(e) => handleConfigChange(gateway, 'enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {gatewayConfig.enabled && (
          <div className="space-y-4">
            {/* Environment Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Environment</label>
                <select
                  value={gatewayConfig.environment}
                  onChange={(e) => handleConfigChange(gateway, 'environment', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="production">Production</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Test Mode</label>
                <label className="relative inline-flex items-center cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    checked={gatewayConfig.testMode}
                    onChange={(e) => handleConfigChange(gateway, 'testMode', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ml-3 text-sm text-gray-700">
                    {gatewayConfig.testMode ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>

            {/* Razorpay Specific Fields */}
            {gateway === 'razorpay' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={gatewayConfig.keyId}
                    onChange={(e) => handleConfigChange(gateway, 'keyId', e.target.value)}
                    placeholder="rzp_test_..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key Secret <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={gatewayConfig.keySecret}
                      onChange={(e) => handleConfigChange(gateway, 'keySecret', e.target.value)}
                      placeholder="Enter key secret"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecrets(prev => ({ ...prev, [gateway]: !prev[gateway] }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showSecret ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webhook Secret
                  </label>
                  <div className="relative">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={gatewayConfig.webhookSecret}
                      onChange={(e) => handleConfigChange(gateway, 'webhookSecret', e.target.value)}
                      placeholder="Enter webhook secret"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecrets(prev => ({ ...prev, [gateway]: !prev[gateway] }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showSecret ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* PhonePe Specific Fields */}
            {gateway === 'phonepe' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Merchant ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={gatewayConfig.merchantId}
                    onChange={(e) => handleConfigChange(gateway, 'merchantId', e.target.value)}
                    placeholder="Enter merchant ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Salt Key <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showSecret ? 'text' : 'password'}
                        value={gatewayConfig.saltKey}
                        onChange={(e) => handleConfigChange(gateway, 'saltKey', e.target.value)}
                        placeholder="Enter salt key"
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecrets(prev => ({ ...prev, [gateway]: !prev[gateway] }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showSecret ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Salt Index
                    </label>
                    <input
                      type="text"
                      value={gatewayConfig.saltIndex}
                      onChange={(e) => handleConfigChange(gateway, 'saltIndex', e.target.value)}
                      placeholder="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Test Connection Button */}
            <div className="flex justify-end pt-2 border-t border-gray-200">
              <button
                onClick={() => handleTestConnection(gateway)}
                disabled={isTesting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isTesting ? (
                  <>
                    <FaSpinner className="animate-spin" /> Testing...
                  </>
                ) : (
                  <>
                    <FaPlug /> Test Connection
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Payment Gateway Configuration</h2>
          <p className="text-sm text-gray-600 mt-1">Configure Razorpay and PhonePe payment gateways</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <>
                <FaSpinner className="animate-spin" /> Saving...
              </>
            ) : (
              <>
                <FaSave /> Save Configuration
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <FaShieldAlt className="text-yellow-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">Security Notice</p>
            <p className="text-xs text-yellow-700 mt-1">
              All credentials are encrypted and stored securely. Never share your API keys or secrets.
            </p>
          </div>
        </div>
      </div>

      <GatewaySection
        gateway="razorpay"
        title="Razorpay"
        icon={FaKey}
      />

      <GatewaySection
        gateway="phonepe"
        title="PhonePe"
        icon={FaKey}
      />
    </div>
  );
};

export default PaymentGatewayConfig;


